import { useState, useCallback } from 'react';
import insightsService from '../../../services/insights.service';
import { debugError } from '../../../utils/debugLog';

/**
 * useTemplateProgressionInsights Hook
 * Manages template progression insights state and loading
 */
export const useTemplateProgressionInsights = (
  isAuthenticated,
  authLoading
) => {
  const [progressionBatches, setProgressionBatches] = useState([]);
  const [progressionBatchInsights, setProgressionBatchInsights] =
    useState(null);
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [selectedProgressionBatchId, setSelectedProgressionBatchId] =
    useState(null);
  const [error, setError] = useState(null);

  const loadProgressionBatchInsights = useCallback(
    async batchId => {
      if (!isAuthenticated || authLoading || !batchId) {
        return;
      }
      try {
        setProgressionLoading(true);
        setError(null);
        const response = await insightsService.getBatchInsights(batchId);
        setProgressionBatchInsights(response);
      } catch (err) {
        debugError('Failed to load progression batch insights:', err);
        setError(err.message || 'Failed to load progression batch insights');
      } finally {
        setProgressionLoading(false);
      }
    },
    [isAuthenticated, authLoading]
  );

  const loadLatestProgressionBatches = useCallback(async () => {
    if (!isAuthenticated || authLoading) return;

    try {
      setProgressionLoading(true);
      setError(null);

      const response = await insightsService.listBatches({
        insight_type: 'template_version_comparison',
        latest: true,
      });

      if (response && response.batches && response.batches.length > 0) {
        setProgressionBatches(response.batches);
        const latestProgression = response.batches[0];
        setSelectedProgressionBatchId(latestProgression.batch_id);
        await loadProgressionBatchInsights(latestProgression.batch_id);
      }
    } catch (err) {
      debugError('Failed to load progression batches:', err);
      setError(err.message || 'Failed to load progression batches');
    } finally {
      setProgressionLoading(false);
    }
  }, [isAuthenticated, authLoading, loadProgressionBatchInsights]);

  return {
    progressionBatches,
    progressionBatchInsights,
    progressionLoading,
    selectedProgressionBatchId,
    error,
    setProgressionBatches,
    setSelectedProgressionBatchId,
    loadProgressionBatchInsights,
    loadLatestProgressionBatches,
  };
};
